import { AgEventAPI } from "@ai360/core";
import { MessageDescriptor } from "@ai360/core/intl";

import { IOption } from "~/core/components/select-input/model";
import { messages } from "../i18n-messages";

export interface IProductOptionsInput {
    formatMessage: (descriptor: MessageDescriptor, values?: Record<string, unknown>) => string;
    /** False while the Agvance queries are in flight. */
    isRestrictionSettled: boolean;
    /** AgBytes product guids linked in Agvance. null means unrestricted. */
    linkedProductGuids: Set<string> | null;
    products: AgEventAPI.IAvailableProduct[];
    selectedProductGuid: string | null;
    selectedProductName: string | null;
}

export interface IProductOptionsResult {
    isSelectedProductUnlinked: boolean;
    productOptions: IOption[];
}

const toOptions = (products: AgEventAPI.IAvailableProduct[]): IOption[] =>
    products.map((product) => ({ label: product.name, value: product.productGuid }));

/**
 * Builds the Defined Legend Product dropdown. An unlinked saved product stays in the list under its own
 * name so editing does not blank the field — the caller flags it via isSelectedProductUnlinked, not the label.
 */
export const getProductOptions = ({
    formatMessage,
    isRestrictionSettled,
    linkedProductGuids,
    products,
    selectedProductGuid,
    selectedProductName,
}: IProductOptionsInput): IProductOptionsResult => {
    const candidates = products ?? [];

    const settledLinkedProductGuids = isRestrictionSettled ? linkedProductGuids : null;

    const isSelectedProductUnlinked = Boolean(
        settledLinkedProductGuids != null &&
            selectedProductGuid &&
            !settledLinkedProductGuids.has(selectedProductGuid)
    );

    if (settledLinkedProductGuids == null) {
        return { isSelectedProductUnlinked, productOptions: toOptions(candidates) };
    }

    const options = toOptions(
        candidates.filter((product) => settledLinkedProductGuids.has(product.productGuid))
    );

    if (!selectedProductGuid || settledLinkedProductGuids.has(selectedProductGuid)) {
        return { isSelectedProductUnlinked, productOptions: options };
    }

    const retainedProductName =
        candidates.find((product) => product.productGuid === selectedProductGuid)?.name ??
        selectedProductName ??
        formatMessage(messages.unknownProduct);

    return {
        isSelectedProductUnlinked,
        productOptions: [{ label: retainedProductName, value: selectedProductGuid }, ...options],
    };
};
